import React from 'react';
import { ToggleFeatures } from 'react-enable';
import { QueryClient } from 'react-query';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';

import { IS_DEV, PLUGIN_ROOT } from 'consts';
import CreateJob from 'projects/Forecasting/Create';
import EditJob from 'projects/Forecasting/Edit';
import InteractiveCreateJob from 'projects/Forecasting/InteractiveCreate';
import InteractiveEditJob from 'projects/Forecasting/InteractiveEdit';
import { ForecastingTabs } from 'projects/Forecasting/Tabs/ForecastingTabs';
import ViewJob from 'projects/Forecasting/View';
import Home from 'projects/Home/Home';
import CreateOutlier from 'projects/Outliers/Create';
import OutlierList from 'projects/Outliers/List/OutlierList';
import ViewOutlier from 'projects/Outliers/View';
import Investigation from 'projects/Sift/Investigation/Investigation';
import { InvestigationTabs } from 'projects/Sift/Tabs/InvestigationTabs';
import { isUserActionAllowed, UserActions } from 'utils/authorization';

interface AuthenticatedRouteProps {
  requiredAction: (typeof UserActions)[keyof typeof UserActions];
  fallbackPath: string;
  children: React.ReactNode;
}

function AuthenticatedRoute({ requiredAction, fallbackPath, children }: AuthenticatedRouteProps): React.JSX.Element {
  return isUserActionAllowed(requiredAction) ? <>{children}</> : <Navigate to={`${PLUGIN_ROOT}${fallbackPath}`} />;
}

export const queryClient = new QueryClient();

function EditJobRoute({ tenantData }: { tenantData: any }): React.JSX.Element {
  const { id } = useParams();
  return (
    <AuthenticatedRoute requiredAction={UserActions.ForecastingWrite} fallbackPath={`/metric-forecast/${id}`}>
      <EditJob tenantInfo={tenantData} />
    </AuthenticatedRoute>
  );
}

function InteractiveEditJobRoute({ tenantData }: { tenantData: any }): React.JSX.Element {
  const { id } = useParams();
  return (
    <AuthenticatedRoute requiredAction={UserActions.ForecastingWrite} fallbackPath={`/metric-forecast/${id}`}>
      <InteractiveEditJob tenantInfo={tenantData} />
    </AuthenticatedRoute>
  );
}

export function AppContent({ tenantData }: { tenantData: any }): React.JSX.Element {
  return (
    <>
      <Routes>
        <Route path={`home`} element={<Home />} />

        <Route path={`metric-forecast`} element={<ForecastingTabs view={'list'} />} />
        <Route
          path={`metric-forecast/holiday`}
          element={
            <AuthenticatedRoute requiredAction={UserActions.ForecastingWrite} fallbackPath="/metric-forecast">
              <ForecastingTabs view="holiday" />
            </AuthenticatedRoute>
          }
        />
        <Route
          path={`metric-forecast/holiday/create`}
          element={
            <AuthenticatedRoute requiredAction={UserActions.ForecastingWrite} fallbackPath="/metric-forecast">
              <ForecastingTabs view="holiday-create" />
            </AuthenticatedRoute>
          }
        />
        <Route
          path={`metric-forecast/holiday/:id/edit`}
          element={
            <AuthenticatedRoute requiredAction={UserActions.ForecastingWrite} fallbackPath="/metric-forecast">
              <ForecastingTabs view="holiday-edit" />
            </AuthenticatedRoute>
          }
        />
        <Route
          path={`metric-forecast/holiday/:id`}
          element={
            <AuthenticatedRoute requiredAction={UserActions.ForecastingWrite} fallbackPath="/metric-forecast">
              <ForecastingTabs view="holiday-details" />
            </AuthenticatedRoute>
          }
        />

        <Route
          path={`metric-forecast/create`}
          element={
            <AuthenticatedRoute requiredAction={UserActions.ForecastingWrite} fallbackPath="/metric-forecast">
              <CreateJob tenantInfo={tenantData} />
            </AuthenticatedRoute>
          }
        />
        <Route path={`metric-forecast/:id/edit`} element={<EditJobRoute tenantData={tenantData} />} />
        <Route
          path={`metric-forecast/:id/edit-interactive`}
          element={<InteractiveEditJobRoute tenantData={tenantData} />}
        />
        <Route
          path={`metric-forecast/:id`}
          element={
            <AuthenticatedRoute requiredAction={UserActions.ForecastingRead} fallbackPath="/metric-forecast">
              <ViewJob />
            </AuthenticatedRoute>
          }
        />
        <Route
          path={`metric-forecast/create-interactive`}
          element={
            <AuthenticatedRoute requiredAction={UserActions.ForecastingWrite} fallbackPath="/metric-forecast">
              <InteractiveCreateJob tenantInfo={tenantData} />
            </AuthenticatedRoute>
          }
        />

        {/* keeping these holiday routes for backwards compatability */}
        <Route
          path={`holiday`}
          element={
            <AuthenticatedRoute requiredAction={UserActions.ForecastingWrite} fallbackPath="/metric-forecast">
              <ForecastingTabs view="holiday" />
            </AuthenticatedRoute>
          }
        />
        <Route
          path={`holiday/create`}
          element={
            <AuthenticatedRoute requiredAction={UserActions.ForecastingWrite} fallbackPath="/metric-forecast">
              <ForecastingTabs view="holiday-create" />
            </AuthenticatedRoute>
          }
        />
        <Route
          path={`holiday/:id/edit`}
          element={
            <AuthenticatedRoute requiredAction={UserActions.ForecastingWrite} fallbackPath="/metric-forecast">
              <ForecastingTabs view="holiday-edit" />
            </AuthenticatedRoute>
          }
        />
        <Route
          path={`holiday/:id`}
          element={
            <AuthenticatedRoute requiredAction={UserActions.ForecastingWrite} fallbackPath="/metric-forecast">
              <ForecastingTabs view="holiday-details" />
            </AuthenticatedRoute>
          }
        />

        <Route path={`outlier-detector`} element={<OutlierList />} />
        <Route
          path={`outlier-detector/create`}
          element={
            <AuthenticatedRoute requiredAction={UserActions.OutliersWrite} fallbackPath="/outlier-detector">
              <CreateOutlier tenantInfo={tenantData} />
            </AuthenticatedRoute>
          }
        />
        <Route
          path={`outlier-detector/:id`}
          element={
            <AuthenticatedRoute requiredAction={UserActions.OutliersRead} fallbackPath="/outlier-detector">
              <ViewOutlier />
            </AuthenticatedRoute>
          }
        />

        <Route path={`investigations`} element={<InvestigationTabs view="list" />} />
        <Route
          path={`investigations/configuration`}
          element={
            <AuthenticatedRoute requiredAction={UserActions.SiftWrite} fallbackPath="/investigations">
              <InvestigationTabs view="config" />
            </AuthenticatedRoute>
          }
        />
        <Route
          path={`investigations/create`}
          element={
            <AuthenticatedRoute requiredAction={UserActions.SiftWrite} fallbackPath="/investigations">
              <InvestigationTabs view="create" />
            </AuthenticatedRoute>
          }
        />

        <Route path={`investigations/:investigationId`} element={<Investigation />} />
        <Route path={`investigations/:investigationId/:analysisId`} element={<Investigation />} />

        <Route path={`*`} element={<Navigate to={`${PLUGIN_ROOT}/home`} />} />
      </Routes>
      {IS_DEV ? <ToggleFeatures /> : null}
    </>
  );
}
